import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { syncLeadToGhl } from "@/lib/ghl.functions";
import { logSubmissionAttempt } from "@/lib/submission-log.functions";
import { submitSurveyLead } from "@/lib/survey-submit.functions";
import { detectTimezone } from "@/lib/timezone";
import { computeLeadScoreV1, formatScore } from "@/lib/lead-score-v1";
import { saveStickyContact, stickyCalendarParams } from "@/lib/sticky-contact";
import { trackEvent, getSessionId } from "@/hooks/use-funnel-tracking";
import { captureAttribution, getAttribution } from "@/lib/attribution";
import { fireLeadEvent, persistLeadEvent } from "@/lib/meta-pixel";
import { fireScheduleConversion } from "@/lib/meta-schedule";
import { buildThankYouRedirect } from "@/lib/booking-redirect";
import { logBookingRedirect } from "@/lib/booking-log.functions";
import {
  logEvent,
  markSurveyStartOnce,
  getAnalyticsIdentity,
  VARIANT_ID_KEY,
  SESSION_ID_KEY,
  PREVIEW_MODE_KEY,
} from "@/lib/analytics";
import { X, ArrowRight, ArrowLeft, Check, ChevronDown, Loader2, AlertTriangle } from "lucide-react";
import { z } from "zod";

import {
  SURVEY_STEPS,
  DQ_MODAL_CONTENT,
  buildWhen,
  renderQuestionText,
  type SurveyAnswers as Answers,
  type SurveyStep as Step,
} from "@/lib/survey-config";
import {
  evaluateSurveyRouting,
  routingOutcomeConfig,
  carePlanAnswer,
} from "@/lib/survey-routing";
import { recordSurveyResponse } from "@/lib/survey-response.functions";

const STEPS: Step[] = SURVEY_STEPS;

// Match the landing page typography exactly so the survey feels like the
// same page (no serif / thin display font).
const SANS_STACK =
  "'Poppins', 'Inter', ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif";


// Fake progress - heavily front-loaded so early answers feel like huge momentum,
// then increments shrink so the user always feels "almost done."
const FAKE_PROGRESS = [28, 52, 75, 83, 89, 94, 96, 98, 99];


const emailSchema = z
  .string()
  .trim()
  .max(255)
  .email()
  .regex(/^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/);
const nameSchema = z
  .string()
  .trim()
  .regex(/^[A-Za-zÀ-ÿ'’-]+(?:\s+[A-Za-zÀ-ÿ'’-]+)+$/);
const phoneSchema = z
  .string()
  .transform((v) => v.replace(/\D/g, ""))
  .refine((d) => d.length === 10 || (d.length === 11 && d.startsWith("1")));

// Country dial codes offered on the phone step. +1 (USA) is the default so
// people don't type their own country code and break the formatting.
const DIAL_CODES: { code: string; label: string }[] = [
  { code: "+1", label: "United States / Canada (+1)" },
  { code: "+44", label: "United Kingdom (+44)" },
  { code: "+61", label: "Australia (+61)" },
  { code: "+64", label: "New Zealand (+64)" },
  { code: "+353", label: "Ireland (+353)" },
  { code: "+49", label: "Germany (+49)" },
  { code: "+33", label: "France (+33)" },
  { code: "+34", label: "Spain (+34)" },
  { code: "+39", label: "Italy (+39)" },
  { code: "+31", label: "Netherlands (+31)" },
  { code: "+32", label: "Belgium (+32)" },
  { code: "+41", label: "Switzerland (+41)" },
  { code: "+43", label: "Austria (+43)" },
  { code: "+45", label: "Denmark (+45)" },
  { code: "+46", label: "Sweden (+46)" },
  { code: "+47", label: "Norway (+47)" },
  { code: "+358", label: "Finland (+358)" },
  { code: "+351", label: "Portugal (+351)" },
  { code: "+352", label: "Luxembourg (+352)" },
  { code: "+354", label: "Iceland (+354)" },
  { code: "+81", label: "Japan (+81)" },
  { code: "+82", label: "South Korea (+82)" },
  { code: "+65", label: "Singapore (+65)" },
  { code: "+852", label: "Hong Kong (+852)" },
  { code: "+972", label: "Israel (+972)" },
];
const DEFAULT_DIAL = "+1";

const intlPhoneSchema = z
  .string()
  .transform((v) => v.replace(/\D/g, ""))
  .refine((d) => d.length >= 6 && d.length <= 15);

const formatPhone = (raw: string) => {
  let d = raw.replace(/\D/g, "");
  // Drop a leading US/Canada "1" (typed or autofilled "+1 ...") — the dial
  // picker already supplies the country code. US area codes never start
  // with 1, so this never eats a real digit.
  if (d.startsWith("1")) d = d.slice(1);
  d = d.slice(0, 10);
  if (d.length === 0) return "";
  if (d.length < 4) return `(${d}`;
  if (d.length < 7) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
};

/** Landing-page / split-test context attached to every survey response row. */
const surveyContext = () => {
  if (typeof window === "undefined") return {};
  const variantLabel = sessionStorage.getItem("inflow_variant_label") as
    | "A"
    | "B"
    | "C"
    | "D"
    | null;
  const sid = sessionStorage.getItem(SESSION_ID_KEY) || getSessionId();
  return {
    variant_id: sessionStorage.getItem(VARIANT_ID_KEY) || null,
    variant_label: variantLabel,
    session_id: sid,
    landing_path: getAttribution().last.landing_path ?? null,
    path: window.location.pathname,
    referrer: document.referrer || null,
  };
};

const safeUuid = () => {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  return `${Date.now()}-${Math.random()}`.replace(/[^a-z0-9-]/gi, "");
};

const limitText = (value: string | null | undefined, max: number): string | null => {
  if (value == null) return null;
  const trimmed = value.trim();
  return trimmed ? trimmed.slice(0, max) : null;
};

// Helpers to safely coerce SurveyAnswerValue (string | string[]) to the
// shape a given consumer expects.
const asString = (v: unknown): string => (typeof v === "string" ? v : "");
const asArray = (v: unknown): string[] => (Array.isArray(v) ? v : []);

/**
 * Single source of truth for per-step validation. Used both when the user
 * clicks Continue AND as a final sweep right before submit, so no answer can
 * ever slip through empty/invalid no matter how fast the form is completed.
 */
function validateAnswer(
  step: Step,
  raw: unknown,
  dial: string,
): string | null {
  if (step.kind === "choice") {
    return asString(raw).trim() ? null : "Please pick an option.";
  }
  if (step.kind === "multi_choice") {
    const min = step.minSelected ?? (step.required ? 1 : 0);
    return asArray(raw).length >= min ? null : `Please select at least ${min}.`;
  }
  const value = asString(raw).trim();
  if (step.required && !value) return "This one's required.";
  if (!value) return null;
  if (step.id === "name") {
    return nameSchema.safeParse(value).success
      ? null
      : "Please enter your first and last name.";
  }
  if (step.id === "phone") {
    const digits = value.replace(/\D/g, "");
    const isDefault = value.startsWith(DEFAULT_DIAL)
      ? true
      : /^\+/.test(value)
        ? false
        : dial === DEFAULT_DIAL;
    const local = isDefault ? digits.replace(/^1/, "") : digits;
    const valid = isDefault
      ? phoneSchema.safeParse(local).success
      : intlPhoneSchema.safeParse(digits).success;
    return valid
      ? null
      : isDefault
        ? "Please enter a valid 10-digit phone number."
        : "Please enter a valid phone number.";
  }
  if (step.kind === "input" && step.type === "email") {
    return emailSchema.safeParse(value).success ? null : "Please enter a valid email.";
  }
  return null;
}

export function Survey({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState<Answers>({});
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [calendarLoaded, setCalendarLoaded] = useState(false);
  const [err, setErr] = useState("");
  const [multiSel, setMultiSel] = useState<string[]>([]);
  const [dq, setDq] = useState(false);
  const [dial, setDial] = useState(DEFAULT_DIAL);
  const inputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  // Guards against double-fire: a fast double click / double Enter used to run
  // advance() twice within the 220ms transition window and jump two steps,
  // silently skipping a question. Unlocked once the new step is mounted.
  const advanceLock = useRef(false);
  const submitLock = useRef(false);

  useEffect(() => {
    advanceLock.current = false;
  }, [idx]);
  
  

  const visibleSteps = useMemo(
    () => STEPS.filter((s) => {
      const when = buildWhen(s.conditions);
      return !when || when(answers);
    }),
    [answers],
  );
  const step = visibleSteps[idx];
  const total = visibleSteps.length;
  const displayProgress = done
    ? 100
    : FAKE_PROGRESS[Math.min(idx, FAKE_PROGRESS.length - 1)] ?? 95;

  useEffect(() => {
    if (open) {
      setIdx(0);
      setAnswers({});
      setDone(false);
      setCalendarLoaded(false);
      setErr("");
      setDq(false);
      advanceLock.current = false;
      submitLock.current = false;
      // Refresh attribution snapshot the moment the survey opens so any
      // _fbp/_fbc cookies set after the original landing are captured.
      captureAttribution();
      void trackEvent("survey_start");
      // Split-test: fire variant survey_start at most once per session.
      if (markSurveyStartOnce()) void logEvent("survey_start");
    }
  }, [open]);

  useEffect(() => {
    if (!open || !step) return;
    if (step.kind === "input") {
      setTimeout(() => inputRef.current?.focus(), 80);
    } else if (step.kind === "textarea") {
      setTimeout(() => textareaRef.current?.focus(), 80);
    } else if (step.kind === "multi_choice") {
      setMultiSel(asArray(answers[step.id]));
    }
  }, [idx, open, step, answers]);

  // Load GHL booking widget resize script once the calendar mounts.
  useEffect(() => {
    if (!done) return;
    const script = document.createElement("script");
    script.src = "https://link.msgsndr.com/js/form_embed.js";
    script.type = "text/javascript";
    script.async = true;
    document.body.appendChild(script);
    return () => {
      if (script.parentNode) script.parentNode.removeChild(script);
    };
  }, [done]);

  // Fallback: hide spinner after 4s even if iframe onLoad is delayed.
  useEffect(() => {
    if (!done) return;
    const t = setTimeout(() => setCalendarLoaded(true), 4000);
    return () => clearTimeout(t);
  }, [done]);

  // Meta "Schedule": the LeadConnector calendar widget posts a message to the
  // parent window when an appointment is actually booked. Fire the browser
  // pixel + the server-side Conversions API with one shared event id so Meta
  // dedupes them into a single Schedule conversion.
  useEffect(() => {
    if (!done) return;
    let fired = false;
    const onMessage = (e: MessageEvent) => {
      if (fired) return;
      const origin = String(e.origin || "");
      if (!/(leadconnectorhq|msgsndr)\.com$/.test(new URL(origin || "https://x.invalid").hostname)) return;
      let raw = "";
      try {
        raw = typeof e.data === "string" ? e.data : JSON.stringify(e.data ?? "");
      } catch {
        raw = "";
      }
      if (!/appointment|booked|booking_?success|msgsndr:success|slot_?booked/i.test(raw)) return;
      if (/resize|height|scroll/i.test(raw) && !/appointment|booked|success/i.test(raw)) return;
      fired = true;

      const fullName = asString(answers.name).trim();
      const parts = fullName.split(/\s+/);
      const routing = evaluateSurveyRouting(answers);
      const email = asString(answers.email) || null;
      const phone = asString(answers.phone) || null;
      const firstName = parts[0] ?? null;
      const lastName = parts.slice(1).join(" ") || null;
      // Schedule CAPI fires here, BEFORE the redirect — and only for Tier 3
      // (demo calendar) bookings, per the gate inside fireScheduleConversion.
      const schedule = fireScheduleConversion({
        email,
        phone,
        firstName,
        lastName,
        tier: routing.tier,
        outcome: routing.outcome,
      });

      // Redirect BOTH calendars (intro + demo) to the external thank-you page,
      // carrying whatever contact + UTM/click attribution exists. Missing
      // values are skipped rather than sent empty, so the URL always stays valid.
      const plan = buildThankYouRedirect({ email, phone, firstName, lastName });

      // Verification record: what was booked, whether Schedule fired, and the
      // exact params handed to the thank-you page.
      void logBookingRedirect({
        data: {
          session_id: (() => {
            try {
              return getSessionId();
            } catch {
              return null;
            }
          })(),
          calendar: routing.tier === "3" ? "demo" : "intro",
          tier: routing.tier ?? null,
          outcome: routing.outcome ?? null,
          schedule_fired: schedule.fired,
          schedule_skipped_reason: schedule.reason,
          email,
          phone,
          first_name: firstName,
          last_name: lastName,
          redirect_url: plan.url,
          params: plan.params as Record<string, string>,
          missing_params: plan.missing as string[],
          page_url: window.location.href,
        },
      }).catch(() => {});

      // Brief delay so the browser pixel + CAPI request can leave the page
      // before the navigation tears everything down.
      window.setTimeout(() => {
        window.location.href = plan.url;
      }, 800);
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [done, answers]);


  const submit = async (finalAnswers: Answers) => {
    if (submitLock.current) return;
    // Final safety sweep: every visible step must hold a valid answer. If one
    // is missing (fast clicking, autofill race, restored state), jump the user
    // back to that exact step with an inline error instead of failing server-side.
    const finalVisible = STEPS.filter((s) => {
      const when = buildWhen(s.conditions);
      return !when || when(finalAnswers);
    });
    for (let i = 0; i < finalVisible.length; i++) {
      const s = finalVisible[i]!;
      const problem = validateAnswer(s, finalAnswers[s.id], dial);
      if (problem) {
        setAnswers(finalAnswers);
        setBusy(false);
        advanceLock.current = false;
        setIdx(i);
        setErr(problem);
        return;
      }
    }
    submitLock.current = true;
    setBusy(true);
    setErr("");
    const role = asString(finalAnswers.role);
    const area = asString(finalAnswers.area);
    const deals12mo = asString(finalAnswers.deals_12mo);
    const websiteStatus = asString(finalAnswers.website_status);
    const website = asString(finalAnswers.website).trim();
    const followupSystem = asString(finalAnswers.followup_system);
    const motivation = asString(finalAnswers.motivation);
    const leadGenSources = asArray(finalAnswers.lead_gen_sources);
    // Clinic application fields (current question set: Q1–Q5).
    const clinicName = asString(finalAnswers.clinic_name).trim();
    const treatments = asArray(finalAnswers.treatments);
    const lowestCarePlanPrice =
      carePlanAnswer(finalAnswers) || asString(finalAnswers.lowest_care_plan_price);
    const monthlyRevenue = asString(finalAnswers.monthly_revenue);
    const biggestChallenge = asString(finalAnswers.biggest_challenge).trim();
    const clinicOwner = asString(finalAnswers.clinic_owner);
    const offersTreatment = asString(finalAnswers.offers_treatment);
    const clinicStage = asString(finalAnswers.clinic_stage);
    // Hidden scoring + routing snapshot (Tier 3 / Tier 2 / DQ).
    const routing = evaluateSurveyRouting(finalAnswers);
    // Lead scoring v1 — snapshotted at submission time from the answers given.
    const scoring = computeLeadScoreV1(finalAnswers as Record<string, string | string[] | null | undefined>);
    const leadScore = scoring.leadScore;
    const inquiryId = safeUuid();
    const payload = {
      id: inquiryId,
      name: limitText(asString(finalAnswers.name), 200) || "",
      email: limitText(asString(finalAnswers.email), 255) || "",
      phone: limitText(asString(finalAnswers.phone), 40),
      price_range: limitText(monthlyRevenue, 100),
      interest: limitText(treatments.join(", ") || role, 100),
      clinic_name: limitText(clinicName, 150),
      treatments: treatments.length ? treatments : null,
      lowest_care_plan_price: limitText(lowestCarePlanPrice, 60),
      monthly_revenue: limitText(monthlyRevenue, 60),
      biggest_challenge: limitText(biggestChallenge, 1500),
      lead_score: leadScore,
      raw_average: scoring.rawAverage,
      questions_answered: scoring.questionsAnswered,
      scoring_version: scoring.scoringVersion,
      message: limitText([
        `Lead score: ${formatScore(leadScore)}/10`,
        clinicOwner ? `Clinic owner: ${clinicOwner}` : null,
        offersTreatment ? `Offers treatment: ${offersTreatment}` : null,
        clinicStage ? `Clinic stage: ${clinicStage}` : null,
        clinicName ? `Clinic: ${clinicName}` : null,
        treatments.length ? `Treatments: ${treatments.join(", ")}` : null,
        lowestCarePlanPrice ? `Lowest care plan price: ${lowestCarePlanPrice}` : null,
        monthlyRevenue ? `Monthly revenue: ${monthlyRevenue}` : null,
        biggestChallenge ? `Biggest challenge: ${biggestChallenge}` : null,
        area ? `Area serviced: ${area}` : null,
        role ? `Role: ${role}` : null,
        deals12mo ? `Deals (12mo): ${deals12mo}` : null,
        leadGenSources.length ? `Lead gen sources: ${leadGenSources.join(", ")}` : null,
        websiteStatus ? `Website status: ${websiteStatus}` : null,
        website ? `Website URL: ${website}` : null,
        followupSystem ? `Liquid funds available: ${followupSystem}` : null,
        motivation ? `$100 investment answer: ${motivation}` : null,
      ]
        .filter(Boolean)
        .join("\n"), 2000),
    };


    // Marketing attribution from the original ad click
    const attribution = getAttribution();
    const nameParts = (payload.name || "").trim().split(/\s+/);
    const lead = persistLeadEvent({
      email: payload.email,
      phone: payload.phone || undefined,
      firstName: nameParts[0],
      lastName: nameParts.slice(1).join(" ") || undefined,
      tier: routing.tier,
      outcome: routing.outcome,
    });
    const insertPayload = {
      ...payload,
      utm_source: limitText(attribution.last.utm_source, 200),
      utm_medium: limitText(attribution.last.utm_medium, 200),
      utm_campaign: limitText(attribution.last.utm_campaign, 300),
      utm_content: limitText(attribution.last.utm_content, 300),
      utm_term: limitText(attribution.last.utm_term, 200),
      utm_campaign_id: limitText(attribution.last.utm_campaign_id, 120),
      utm_adset_id: limitText(attribution.last.utm_adset_id, 120),
      utm_ad_id: limitText(attribution.last.utm_ad_id, 120),
      fbclid: limitText(attribution.last.fbclid, 500),
      gclid: limitText(attribution.last.gclid, 500),
      landing_path: limitText(attribution.last.landing_path, 1000),
      referrer: limitText(attribution.last.referrer, 1000),
      last_touch_at: attribution.last.touch_at,
      first_utm_source: limitText(attribution.first.utm_source, 200),
      first_utm_medium: limitText(attribution.first.utm_medium, 200),
      first_utm_campaign: limitText(attribution.first.utm_campaign, 300),
      first_utm_content: limitText(attribution.first.utm_content, 300),
      first_utm_term: limitText(attribution.first.utm_term, 200),
      first_utm_campaign_id: limitText(attribution.first.utm_campaign_id, 120),
      first_utm_adset_id: limitText(attribution.first.utm_adset_id, 120),
      first_utm_ad_id: limitText(attribution.first.utm_ad_id, 120),
      first_fbclid: limitText(attribution.first.fbclid, 500),
      first_gclid: limitText(attribution.first.gclid, 500),
      first_landing_path: limitText(attribution.first.landing_path, 1000),
      first_referrer: limitText(attribution.first.referrer, 1000),
      first_touch_at: attribution.first.touch_at,
      fbp: limitText(attribution.fbp, 200),
      fbc: limitText(attribution.fbc, 500),
      meta_event_id: limitText(lead.eventID, 100),
    };

    const sessionId = typeof window !== "undefined" ? getSessionId() : null;
    const path = typeof window !== "undefined" ? window.location.pathname : null;
    const timezone = detectTimezone();
    // Split-test label assigned on /apply. Sent so GHL gets its LP-<variant>
    // tag and so the server can resolve the variant even without a cached id.
    const variantLabel =
      typeof window !== "undefined"
        ? ((sessionStorage.getItem("inflow_variant_label") as
            | "A"
            | "B"
            | "C"
            | "D"
            | null) ?? null)
        : null;
    // Split-test context is sent with the submit call so the server records the
    // submission; a client-side insert would be cancelled by the redirect.
    const tracking =
      typeof window !== "undefined" && sessionStorage.getItem(PREVIEW_MODE_KEY) !== "1"
        ? (() => {
            const vid = sessionStorage.getItem(VARIANT_ID_KEY);
            const sid = sessionStorage.getItem(SESSION_ID_KEY);
            const finalSid = sid && sid.trim().length >= 8 ? sid : sessionId;
            if (!finalSid || finalSid.trim().length < 8) return null;
            const identity = getAnalyticsIdentity();
            return {
              variant_id: vid || null,
              variant_label: variantLabel,
              session_id: finalSid,
              visitor_id: identity?.visitorId ?? finalSid,
            };
          })()
        : null;
    // Sticky contact: keep name/email/phone/timezone for the booking page so the
    // appointment attaches to this same lead instead of creating a duplicate.
    saveStickyContact({
      name: payload.name,
      email: payload.email,
      phone: payload.phone,
      timezone,
    });
    const logMeta = {
      session_id: sessionId,
      path,
      email: payload.email || null,
      name: payload.name || null,
      phone: payload.phone || null,
    };

    // GHL payload runs INLINE on the server inside submitSurveyLead so the
    // redirect to the booking step can never cancel the upstream sync.
    const ghlPayload = {
      name: payload.name,
      email: payload.email,
      phone: payload.phone,
      role: treatments.join(", ") || role || null,
      area: clinicName || area || null,
      deals_12mo: monthlyRevenue || deals12mo || null,
      lead_gen_sources: leadGenSources.length ? leadGenSources : null,
      website_status: websiteStatus || null,
      website: website || null,
      followup_system: lowestCarePlanPrice || followupSystem || null,
      motivation: biggestChallenge || motivation || null,
      lead_score: leadScore,
      timezone,
      variant: variantLabel,
      page_url: typeof window !== "undefined" ? window.location.href : null,
      user_agent: typeof navigator !== "undefined" ? navigator.userAgent : null,
      attribution: {
        utm_source: attribution.last.utm_source,
        utm_medium: attribution.last.utm_medium,
        utm_campaign: attribution.last.utm_campaign,
        utm_content: attribution.last.utm_content,
        utm_term: attribution.last.utm_term,
        utm_campaign_id: attribution.last.utm_campaign_id,
        utm_adset_id: attribution.last.utm_adset_id,
        utm_ad_id: attribution.last.utm_ad_id,
        fbclid: attribution.last.fbclid,
        gclid: attribution.last.gclid,
        landing_path: attribution.last.landing_path,
        referrer: attribution.last.referrer,
        fbp: attribution.fbp,
        fbc: attribution.fbc,
      },
      first_attribution: {
        utm_source: attribution.first.utm_source,
        utm_medium: attribution.first.utm_medium,
        utm_campaign: attribution.first.utm_campaign,
        utm_content: attribution.first.utm_content,
        utm_term: attribution.first.utm_term,
        utm_campaign_id: attribution.first.utm_campaign_id,
        utm_adset_id: attribution.first.utm_adset_id,
        utm_ad_id: attribution.first.utm_ad_id,
        fbclid: attribution.first.fbclid,
        gclid: attribution.first.gclid,
        landing_path: attribution.first.landing_path,
        referrer: attribution.first.referrer,
      },
      // Everything the CURRENT survey actually asked (contact fields excluded).
      survey: finalVisible
        .filter((s) => !["name", "phone", "email"].includes(s.id))
        .map((s) => ({
          id: s.id,
          question: renderQuestionText(s.question, finalAnswers).slice(0, 400),
          answer: (finalAnswers[s.id] ?? null) as string | string[] | null,
        })),
      tier: routing.tier,
      routing_outcome: routing.outcome,
    };

    // Every submission (Tier 3 and Tier 2) is also stored as a survey response
    // with its hidden per-question scores, tier and routing outcome.
    const surveyRecord = {
      name: payload.name || null,
      email: payload.email || null,
      phone: payload.phone,
      answers: finalAnswers,
      scores: routing.scores,
      total_score: routing.totalScore,
      tier: routing.tier,
      routing_outcome: routing.outcome,
      dq_reason: routing.dqReason,
      ...surveyContext(),
    };

    const result = await submitSurveyLead({
      data: { inquiry: insertPayload, audit: logMeta, tracking, ghl: ghlPayload, survey: surveyRecord },
    }).catch((e) => {
      console.error("Survey submit request failed", e);
      return { ok: false as const, error: (e as Error)?.message ?? "Submit request failed" };
    });
    setBusy(false);
    if (!result.ok) {
      console.error("Survey submit failed", result.error);
      submitLock.current = false;
      setErr("Something went wrong. Please try again.");
      return;
    }
    // Meta "Lead": QUALIFIED leads only (clinic owner + offers/has equipment
    // for the treatment + $3,000+ care plan == Tier 3). The browser fire uses
    // the same eventID the server CAPI call used, so Meta dedupes them.
    if (routing.tier === "3") {
      void fireLeadEvent(lead);
    }

    // Retry once from the client only if the inline server sync failed, so a
    // lead is never left out of GHL. Success needs no second call.
    if (!result.ghl?.ok) {
      console.error("GHL inline sync failed — retrying from client", result.ghl?.error);
      void syncLeadToGhl({ data: { ...ghlPayload, inquiry_id: inquiryId } })
        .then((res) => {
          const ok = (res as { ok?: boolean })?.ok !== false;
          void logSubmissionAttempt({
            data: {
              ...logMeta,
              stage: "ghl_sync",
              status: ok ? "success" : "failure",
              inquiry_id: inquiryId,
              error_message: ok ? null : ((res as { error?: string })?.error ?? "GHL sync returned not ok"),
              metadata: { contact_id: (res as { contactId?: string | null })?.contactId ?? null },
            },
          }).catch(() => {});
        })
        .catch((e) => {
          console.error("GHL sync retry failed:", e);
          void logSubmissionAttempt({
            data: {
              ...logMeta,
              stage: "ghl_sync",
              status: "failure",
              inquiry_id: inquiryId,
              error_message: (e as Error)?.message?.slice(0, 2000) ?? "GHL sync exception",
            },
          }).catch(() => {});
        });
    }
    setDone(true);
    void trackEvent("survey_complete");
    void logEvent("survey_complete");
  };


  const advance = (value: string | string[]) => {
    if (!step) return;
    if (advanceLock.current || busy) return;
    // Never advance on an invalid/empty answer, whatever the entry point.
    const problem = validateAnswer(step, value, dial);
    if (problem) {
      setErr(problem);
      return;
    }
    advanceLock.current = true;
    const next = { ...answers, [step.id]: value };
    setAnswers(next);
    setErr("");
    void 0;

    void trackEvent("survey_step", { step_index: idx, step_id: step.id });

    // Scoring + routing engine: disqualify as soon as a DQ answer is given so
    // the respondent never reaches the contact step. The response is still
    // recorded in the admin so lead quality can be tracked per landing page.
    const routing = evaluateSurveyRouting(next);
    if (routing.tier === "DQ") {
      void trackEvent("survey_complete");
      void recordSurveyResponse({
        data: {
          answers: next,
          scores: routing.scores,
          total_score: routing.totalScore,
          tier: routing.tier,
          routing_outcome: routing.outcome,
          dq_reason: routing.dqReason,
          ...surveyContext(),
        },
      }).catch((e) => console.error("DQ response record failed", e));
      setDq(true);
      return;
    }

    const nextVisible = STEPS.filter((s) => {
      const when = buildWhen(s.conditions);
      return !when || when(next);
    });
    // Target an absolute index (position of the step just answered + 1) rather
    // than incrementing, so a stale/duplicated call can't compound.
    const answeredAt = nextVisible.findIndex((s) => s.id === step.id);
    const targetIdx = (answeredAt >= 0 ? answeredAt : idx) + 1;
    if (targetIdx > nextVisible.length - 1) {
      void submit(next);
    } else {
      setTimeout(() => setIdx(targetIdx), 220);
    }
  };

  const handleInputNext = () => {
    if (!step) return;
    if (advanceLock.current || busy) return;
    const raw =
      step.kind === "textarea"
        ? textareaRef.current?.value ?? ""
        : inputRef.current?.value ?? "";
    const value = raw.trim();
    if (step.kind !== "input" && step.kind !== "textarea") return;
    if (step.id === "phone") {
      let digits = value.replace(/\D/g, "");
      // Never store a duplicated country code: if the user typed/pasted the
      // "+1" themselves, keep only the 10-digit national number.
      if (dial === DEFAULT_DIAL && digits.startsWith("1")) {
        digits = digits.slice(1);
      }
      advance(`${dial}${digits}`);
      return;
    }
    advance(value);
  };

  return (
    <AnimatePresence>
      {open && (
        dq ? (
          <motion.div
            key="dq-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            style={{ fontFamily: SANS_STACK }}
            className="fixed inset-0 z-[80] bg-obsidian/95 backdrop-blur-sm text-bone overflow-y-auto flex items-start sm:items-center justify-center px-5 py-16 sm:py-6"
          >
            <motion.div
              initial={{ opacity: 0, y: 16, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.28, ease: [0.65, 0, 0.35, 1] }}
              className="relative w-full max-w-xl min-w-0 border border-bone/15 bg-obsidian p-6 sm:p-10"
            >
              <button
                onClick={onClose}
                aria-label="Close"
                className="absolute top-4 right-4 text-bone/55 hover:text-champagne transition-colors"
              >
                <X className="w-5 h-5" strokeWidth={1.3} />
              </button>
              <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight leading-tight text-balance break-words mb-4 pr-8">
                {DQ_MODAL_CONTENT.title}
              </h2>
              <p className="text-bone/70 text-[0.95rem] sm:text-lg leading-relaxed break-words mb-7">
                {DQ_MODAL_CONTENT.body}
              </p>
              <a
                href={DQ_MODAL_CONTENT.ctaHref}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 w-full sm:w-auto px-6 py-4 bg-champagne text-obsidian font-bold tracking-tight hover:opacity-90 transition-opacity"
              >
                {DQ_MODAL_CONTENT.ctaLabel}
                <ArrowRight className="w-4 h-4 shrink-0" strokeWidth={2} />
              </a>
            </motion.div>
          </motion.div>
        ) : done ? (
          <motion.div
            key="done-page"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            style={{ fontFamily: SANS_STACK }}
            className="fixed inset-0 z-[80] bg-obsidian text-bone overflow-y-auto"
          >
            <button
              onClick={onClose}
              aria-label="Close"
              className="fixed top-4 right-4 sm:top-5 sm:right-6 z-10 text-bone/55 hover:text-champagne transition-colors"
            >
              <X className="w-5 h-5" strokeWidth={1.3} />
            </button>
            <div className="w-full max-w-3xl mx-auto px-5 sm:px-6 pt-16 sm:pt-20 pb-16">
              <div className="text-center mb-8 sm:mb-10">
                <div className="mx-auto mb-6 h-12 w-12 rounded-full border border-champagne flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-champagne" strokeWidth={1.6} />
                </div>
                <p className="label-eyebrow text-champagne mb-3">- You're Not Done -</p>
                <h3 className="text-2xl sm:text-4xl md:text-5xl font-extrabold tracking-tight leading-[1.15] mb-3 sm:mb-4">
                  FINAL STEP: Book Your Free Practice Growth Call
                </h3>
                <p className="text-bone/70 leading-relaxed text-[0.95rem] sm:text-base max-w-xl mx-auto">
                  We'll look at your current numbers, show you where you're losing patients in your process, and map out the fastest path to adding $25K-$100K/month in new patient revenue.
                </p>
                <div className="flex justify-center gap-2 mt-4">
                  <motion.div
                    animate={{ y: [0, 6, 0] }}
                    transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                  >
                    <ChevronDown className="w-4 h-4 sm:w-5 sm:h-5 text-champagne" strokeWidth={2} />
                  </motion.div>
                  <motion.div
                    animate={{ y: [0, 6, 0] }}
                    transition={{ duration: 2, repeat: Infinity, ease: "easeInOut", delay: 0.2 }}
                  >
                    <ChevronDown className="w-4 h-4 sm:w-5 sm:h-5 text-champagne" strokeWidth={2} />
                  </motion.div>
                  <motion.div
                    animate={{ y: [0, 6, 0] }}
                    transition={{ duration: 2, repeat: Infinity, ease: "easeInOut", delay: 0.4 }}
                  >
                    <ChevronDown className="w-4 h-4 sm:w-5 sm:h-5 text-champagne" strokeWidth={2} />
                  </motion.div>
                </div>

              </div>
              {(() => {
                const fullName = asString(answers.name).trim();
                const parts = fullName.split(/\s+/);
                const firstName = parts[0] ?? "";
                const lastName = parts.slice(1).join(" ");
                const params = stickyCalendarParams({
                  name: `${firstName} ${lastName}`.trim(),
                  email: asString(answers.email),
                  phone: asString(answers.phone),
                  timezone: detectTimezone(),
                });
                // Tier 3 (clean 3 across Q3/Q4) -> Demo calendar.
                // Tier 2 -> Intro (discovery) calendar.
                const outcome = routingOutcomeConfig(evaluateSurveyRouting(answers));
                const src = `${outcome.target}?${params.toString()}`;
                return (
                  <div className="relative">
                    {!calendarLoaded && (
                      <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-obsidian">
                        <Loader2 className="w-8 h-8 text-champagne animate-spin mb-3" strokeWidth={1.5} />
                        <p className="label-eyebrow text-bone/60">Loading calendar…</p>
                      </div>
                    )}
                    <iframe
                      src={src}
                      title="Book your trial call"
                      id="ghl-calendar-embed"
                      scrolling="no"
                      onLoad={() => setCalendarLoaded(true)}
                      style={{
                        width: "100%",
                        minHeight: "1100px",
                        border: "none",
                        display: "block",
                        opacity: calendarLoaded ? 1 : 0,
                        transition: "opacity 0.4s ease",
                      }}
                    />
                  </div>
                );
              })()}
            </div>
          </motion.div>
        ) : (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.12 }}
          style={{ fontFamily: SANS_STACK }}
          className="fixed inset-0 z-[80] bg-obsidian text-bone overflow-y-auto overscroll-contain"
        >
          {/* Top bar */}
          <div className="absolute top-0 inset-x-0 z-10 flex items-center gap-3 sm:gap-4 px-4 sm:px-6 md:px-10 py-4 sm:py-5 border-b border-bone/10 backdrop-blur bg-obsidian/70">
            <div className="flex-1 h-[2px] bg-bone/10 overflow-hidden rounded-full">
              <motion.div
                className="h-full bg-champagne"
                initial={false}
                animate={{ width: `${displayProgress}%` }}
                transition={{ duration: 0.5, ease: [0.65, 0, 0.35, 1] }}
              />
            </div>
            <button
              onClick={onClose}
              aria-label="Close survey"
              className="text-bone/55 hover:text-champagne transition-colors shrink-0"
            >
              <X className="w-5 h-5" strokeWidth={1.3} />
            </button>
          </div>

          {/* Step body */}
          <div className="min-h-full w-full flex items-start sm:items-center justify-center px-5 sm:px-6 pt-24 sm:pt-28 pb-16 sm:pb-16">
            <AnimatePresence mode="wait">
              {(

                <motion.div
                  key={step.id}
                  initial={{ opacity: 0, y: 14 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -14 }}
                  transition={{ duration: 0.22, ease: [0.65, 0, 0.35, 1] }}
                   className="w-full max-w-2xl min-w-0"
                >
                   {(() => {
                     const qText = renderQuestionText(step.question, answers);
                     // Typeform-style: consistent, readable scale that steps down
                     // for longer questions instead of becoming a wall of text.
                     const sizeClass =
                       qText.length > 110
                         ? "text-[1.05rem] sm:text-xl md:text-2xl"
                         : qText.length > 60
                           ? "text-[1.2rem] sm:text-2xl md:text-3xl"
                           : "text-[1.35rem] sm:text-3xl md:text-[2.15rem]";
                     return (
                       <h2 className={`${sizeClass} font-bold tracking-tight leading-snug text-balance break-words scroll-mt-28 ${step.sub ? "mb-3 sm:mb-4" : "mb-6 sm:mb-8"}`} style={{ fontFamily: SANS_STACK, fontWeight: 700 }}>
                         {qText}
                       </h2>
                     );
                   })()}

                  {step.bullets && step.bullets.length > 0 && (
                    <ul className="mb-6 sm:mb-8 space-y-1.5 text-bone/80 text-[0.95rem] sm:text-lg leading-snug">
                      {step.bullets.map((b) => (
                        <li key={b} className="flex items-start gap-2">
                          <span className="text-champagne mt-[0.15em]">•</span>
                          <span className="min-w-0 break-words">{b}</span>
                        </li>
                      ))}
                    </ul>
                  )}

                  {step.sub && (
                    <p className="text-bone/60 text-[0.9rem] sm:text-lg font-normal leading-relaxed break-words mb-7 sm:mb-10">{step.sub}</p>
                  )}


                  {step.kind === "choice" ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 sm:gap-3">
                      {step.options.map((opt, i) => (
                        <motion.button
                          key={opt.value}
                          initial={{ opacity: 0, y: 8 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.02 + i * 0.02, duration: 0.18 }}
                          onClick={() => advance(opt.value)}
                          className="group w-full min-w-0 text-left px-4 sm:px-6 py-4 sm:py-5 border border-bone/20 bg-bone/[0.02] hover:border-champagne hover:bg-champagne/[0.06] active:bg-champagne/[0.10] transition-all duration-300 flex items-start sm:items-center justify-between gap-3 sm:gap-4"
                        >
                          <span className="min-w-0 flex-1 text-bone text-[0.95rem] sm:text-lg font-medium leading-snug break-words">{opt.label}</span>
                          <ArrowRight className="w-4 h-4 shrink-0 mt-1 sm:mt-0 text-bone/30 group-hover:text-champagne group-hover:translate-x-1 transition-all" strokeWidth={1.4} />
                        </motion.button>
                      ))}
                    </div>
                  ) : step.kind === "multi_choice" ? (
                     <div>
                       <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                        {step.options.map((opt, i) => {
                          const selected = multiSel.includes(opt.value);
                          return (
                            <motion.button
                              key={opt.value}
                              type="button"
                              initial={{ opacity: 0, y: 8 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: 0.02 + i * 0.02, duration: 0.18 }}
                              onClick={() => {
                                setErr("");
                                setMultiSel((cur) =>
                                  cur.includes(opt.value)
                                    ? cur.filter((v) => v !== opt.value)
                                    : [...cur, opt.value],
                                );
                              }}
                               className={`group w-full min-w-0 text-left px-4 sm:px-6 py-3.5 sm:py-5 border transition-all duration-200 flex items-start sm:items-center justify-between gap-3 sm:gap-4 ${
                                selected
                                  ? "border-champagne bg-champagne/[0.10]"
                                  : "border-bone/20 bg-bone/[0.02] hover:border-champagne/80 hover:bg-champagne/[0.06]"
                              }`}
                            >
                               <span className="min-w-0 flex-1 text-bone text-[0.95rem] sm:text-lg font-medium leading-snug break-words">{opt.label}</span>
                              <span
                                className={`shrink-0 mt-0.5 sm:mt-0 w-5 h-5 rounded-sm border flex items-center justify-center transition-colors ${
                                  selected ? "border-champagne bg-champagne" : "border-bone/30"
                                }`}
                                aria-hidden
                              >
                                {selected && <Check className="w-3.5 h-3.5 text-obsidian" strokeWidth={2.5} />}
                              </span>
                            </motion.button>
                          );
                        })}
                      </div>
                      <div className="mt-7 sm:mt-8 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
                        <button
                          onClick={() => {
                            const min = step.minSelected ?? (step.required ? 1 : 0);
                            if (multiSel.length < min) {
                              setErr(`Please select at least ${min}.`);
                              return;
                            }
                            const picked = multiSel;
                            setMultiSel([]);
                            advance(picked);
                          }}
                          disabled={busy}
                          className="w-full sm:w-auto px-9 py-4 rounded-full bg-champagne text-obsidian label-eyebrow hover:bg-bone active:scale-[0.98] transition-all duration-500 disabled:opacity-60"
                        >
                          Continue
                        </button>
                        <p className="label-eyebrow text-bone/40 hidden sm:block">
                          {multiSel.length} selected
                        </p>
                      </div>
                    </div>
                  ) : step.kind === "textarea" ? (
                    <div>
                      <textarea
                        ref={textareaRef}
                        rows={4}
                        placeholder={step.placeholder}
                        defaultValue={asString(answers[step.id])}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                            e.preventDefault();
                            handleInputNext();
                          }
                        }}
                        className="w-full bg-transparent border border-bone/20 rounded-md p-4 text-base sm:text-lg text-bone font-normal focus:outline-none focus:border-champagne transition-colors placeholder:text-bone/25 resize-none"
                      />
                      <div className="mt-6 sm:mt-8 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
                        <button
                          onClick={handleInputNext}
                          disabled={busy}
                          className="w-full sm:w-auto px-9 py-4 rounded-full bg-champagne text-obsidian label-eyebrow hover:bg-bone active:scale-[0.98] transition-all duration-500 disabled:opacity-60"
                        >
                          {busy ? "Sending…" : idx === total - 1 ? "Submit" : "Continue"}

                        </button>
                        <p className="label-eyebrow text-bone/40 hidden sm:block">⌘ + Enter to continue</p>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <div className={step.id === "phone" ? "flex items-end gap-3 min-w-0" : undefined}>
                      {step.id === "phone" && (
                        <select
                          aria-label="Country code"
                          value={dial}
                          onChange={(e) => {
                            setDial(e.target.value);
                            setErr("");
                            if (inputRef.current) {
                              inputRef.current.value =
                                e.target.value === DEFAULT_DIAL
                                  ? formatPhone(inputRef.current.value)
                                  : inputRef.current.value.replace(/\D/g, "");
                            }
                          }}
                          className="shrink-0 min-w-0 w-[4.75rem] sm:w-28 bg-transparent border-b border-bone/25 py-3 sm:py-4 text-xl sm:text-3xl text-bone font-medium focus:outline-none focus:border-champagne transition-colors"
                        >
                          {DIAL_CODES.map((c) => (
                            <option key={c.code} value={c.code} className="text-obsidian text-base">
                              {dial === c.code ? c.code : c.label}
                            </option>
                          ))}
                        </select>
                      )}
                      <input
                        ref={inputRef}
                        type={step.type ?? "text"}
                        placeholder={step.placeholder}
                        defaultValue={
                          step.id === "phone"
                            ? (() => {
                                const d = asString(answers[step.id]).replace(/^\+\d+/, "").replace(/\D/g, "");
                                return dial === DEFAULT_DIAL ? formatPhone(d) : d;
                              })()
                            : asString(answers[step.id])
                        }
                        inputMode={step.id === "phone" ? "tel" : undefined}
                        autoComplete={
                          step.id === "phone"
                            ? "tel"
                            : step.id === "name"
                              ? "name"
                              : step.id === "email"
                                ? "email"
                                : undefined
                        }
                        // No maxLength on phone: autofill often includes "+1"
                        // and a hard cap would truncate before we can strip it.
                        // formatPhone already caps at the 10-digit national number.
                        onChange={(e) => {
                          if (step.id === "phone") {
                            e.currentTarget.value =
                              dial === DEFAULT_DIAL
                                ? formatPhone(e.currentTarget.value)
                                : e.currentTarget.value.replace(/\D/g, "");
                          }
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            handleInputNext();
                          }
                        }}
                        className="w-full bg-transparent border-b border-bone/25 py-3 sm:py-4 text-xl sm:text-3xl text-bone font-medium focus:outline-none focus:border-champagne transition-colors placeholder:text-bone/25"
                      />
                      </div>
                      {step.note && (
                        <p className="mt-3 text-sm text-bone/50">{step.note}</p>
                      )}
                      <div className="mt-7 sm:mt-8 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
                        <button
                          onClick={handleInputNext}
                          disabled={busy}
                          className="w-full sm:w-auto px-9 py-4 rounded-full bg-champagne text-obsidian label-eyebrow hover:bg-bone active:scale-[0.98] transition-all duration-500 disabled:opacity-60"
                        >
                          {busy ? "Sending…" : idx === total - 1 ? "Submit" : "Continue"}
                        </button>
                        <p className="label-eyebrow text-bone/40 hidden sm:block">press Enter ↵</p>
                      </div>
                    </div>
                  )}

                  {err && <p className="mt-6 text-sm text-destructive">{err}</p>}

                  {idx > 0 && (
                    <div className="mt-10 sm:mt-12 flex justify-center">
                      <button
                        onClick={() => {
                          setErr("");
                          setIdx((i) => Math.max(0, i - 1));
                        }}
                        className="inline-flex items-center gap-2 text-bone/50 hover:text-champagne label-eyebrow transition-colors"
                      >
                        <ArrowLeft className="w-4 h-4" strokeWidth={1.4} />
                        Back
                      </button>
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
        )
      )}
    </AnimatePresence>
  );
}
